// Port of goalhorn-standalone-app/src/goalhorns/bundle/naming.py.
// Entry names: "photos/[NN]<safe name>.<ext>" and "songs/[NN]<safe label>.<ext>".

import {
  BUNDLE_PHOTO_DIR,
  BUNDLE_PREFIX_MIN_WIDTH,
  BUNDLE_SONG_DIR,
  MAX_COMPONENT_LEN,
  PHOTO_FALLBACK_NAME,
  SONG_FALLBACK_NAME,
  WINDOWS_ILLEGAL_CHARS,
  WINDOWS_RESERVED_NAMES,
} from "./constants";

const RESERVED_PREFIX = "_";
// Python str.split() whitespace (includes U+001C..U+001F, U+0085 and U+00A0, unlike JS \s).
// Control characters are deliberate: they are part of Python's whitespace set.
/* eslint-disable no-control-regex */
const PY_WHITESPACE_RE =
  /[\t\n\v\f\r\x1c-\x1f \x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/u;
/* eslint-enable no-control-regex */
const WHITESPACE_CONTROL = new Set(["\t", "\n", "\r", "\v", "\f"]);
const CONTROL_CHAR_RE = /^\p{Cc}$/u;
const TRAILING_TRIM_RE = /[ .]+$/u;

/** "[NN]" prefix for a 0-based index out of `total` entries (1-based output). */
export function prefix(index: number, total: number): string {
  if (!Number.isInteger(index) || !Number.isInteger(total) || total < 1) {
    throw new RangeError(`index ${index} out of range for total ${total}`);
  }
  if (index < 0 || index >= total) {
    throw new RangeError(`index ${index} out of range for total ${total}`);
  }
  const width = Math.max(BUNDLE_PREFIX_MIN_WIDTH, String(total).length);
  return `[${String(index + 1).padStart(width, "0")}]`;
}

function stripUnsafe(text: string): string {
  let out = "";
  for (const char of text) {
    if (WHITESPACE_CONTROL.has(char)) {
      out += " ";
    } else if (CONTROL_CHAR_RE.test(char) || WINDOWS_ILLEGAL_CHARS.includes(char)) {
      continue;
    } else {
      out += char;
    }
  }
  return out;
}

/** Python-style code point truncation (not UTF-16 units). */
function truncateCodePoints(text: string, max: number): string {
  return Array.from(text).slice(0, max).join("");
}

/** Make `text` safe as a Windows and POSIX filename component, or return `fallback`. */
export function safeComponent(text: string, fallback: string): string {
  const cleaned = stripUnsafe(text.normalize("NFC"));
  const collapsed = cleaned
    .split(PY_WHITESPACE_RE)
    .filter((part) => part.length > 0)
    .join(" ");
  const trimmed = truncateCodePoints(collapsed, MAX_COMPONENT_LEN).replace(TRAILING_TRIM_RE, "");
  if (trimmed === "") return fallback;
  const base = (trimmed.split(".")[0] ?? "").toUpperCase();
  if (WINDOWS_RESERVED_NAMES.has(base)) return RESERVED_PREFIX + trimmed;
  return trimmed;
}

/** Lowercase ASCII alphanumerics with a leading dot, or "" when nothing is left. */
export function cleanExt(ext: string): string {
  const alnum = ext
    .toLowerCase()
    .split("")
    .filter((char) => /^[a-z0-9]$/.test(char))
    .join("");
  return alnum ? `.${alnum}` : "";
}

export function photoEntry(index: number, total: number, name: string, ext: string): string {
  const stem = safeComponent(name, PHOTO_FALLBACK_NAME);
  return `${BUNDLE_PHOTO_DIR}/${prefix(index, total)}${stem}${cleanExt(ext)}`;
}

export function songEntry(index: number, total: number, label: string, ext: string): string {
  const stem = safeComponent(label, SONG_FALLBACK_NAME);
  return `${BUNDLE_SONG_DIR}/${prefix(index, total)}${stem}${cleanExt(ext)}`;
}
